import { CardClient } from '@atlaskit/link-provider';
import { APIError, type CardAppearance, type ErrorType } from '@atlaskit/linking-common';
import { type JsonLd } from 'json-ld-types';

import { hostNotifier, FailureReasons } from '@canvas/host-notifier';

const RESOLVER_ENDPOINT = '/gateway/api/object-resolver/resolve/batch';

type UpdateListener = () => void;
type UnsubscribeUpdateListener = () => void;

export type SmartLinkClientContract = CardClient & {
  /**
   * Bypasses resolver caches after a mutation and notifies subscribers once the
   * fresh response can be consumed by a synchronous Smart Card reload.
   */
  forceRefreshData(url: string): Promise<JsonLd.Response>;
  /**
   * Subscribes to successful explicit refreshes for one URL.
   */
  subscribeToUpdates(url: string, callback: UpdateListener): UnsubscribeUpdateListener;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const getOptionalErrorType = (value: unknown): ErrorType | undefined =>
  typeof value === 'string' ? (value as ErrorType) : undefined;

const isAPIErrorShape = (
  error: unknown,
): error is Record<string, unknown> & { name: 'APIError' } =>
  isRecord(error) && error.name === 'APIError';

const normalizeResolverError = (error: unknown): unknown => {
  if (!isAPIErrorShape(error) || error.kind !== 'fatal') {
    return error;
  }

  const type = getOptionalErrorType(error.type);

  if (type === 'ResolveUnsupportedError') {
    return error;
  }

  return new APIError(
    'error',
    getOptionalString(error.hostname) ?? '',
    getOptionalString(error.message) ?? 'Unknown resolver failure',
    type,
    getOptionalString(error.extensionKey),
  );
};

const reportResolverCheckpoint = () => {
  hostNotifier.reportExternalCheckpoint({
    source: FailureReasons.SMART_LINK_RESOLVE_FAILURE,
    location: 'databases-smart-link-client',
    endpoint: RESOLVER_ENDPOINT,
  });
};

const readResolverResponse = async (
  response: Response,
  url: string,
): Promise<JsonLd.Response> => {
  let payload: unknown;

  try {
    payload = await response.json();
  } catch {
    throw new APIError('fallback', url, 'Invalid resolver response');
  }

  const result = Array.isArray(payload) ? payload[0] : undefined;

  if (!isRecord(result)) {
    throw new APIError('fallback', url, 'Invalid resolver response');
  }

  if ('error' in result) {
    if (!isRecord(result.error)) {
      throw new APIError('fallback', url, 'Invalid resolver response');
    }

    throw new APIError(
      'fallback',
      url,
      getOptionalString(result.error.message) ?? 'Unknown error',
      getOptionalErrorType(result.error.type),
      getOptionalString(result.error.extensionKey),
    );
  }

  if (!isRecord(result.body)) {
    throw new APIError('fallback', url, 'Invalid resolver response');
  }

  return result.body as unknown as JsonLd.Response;
};

type ListenerResult = { error: unknown; succeeded: false } | { succeeded: true };

const invokeListener = (listener: UpdateListener): ListenerResult => {
  try {
    listener();

    return { succeeded: true };
  } catch (error) {
    return { error, succeeded: false };
  }
};

/**
 * Databases adapter around Atlaskit's Smart Link client. Atlaskit owns ordinary
 * resolution, caching, deduplication, retries, and batching. This class adds
 * only Canvas failure reporting and the explicit refresh notification contract
 * required after Databases mutates a linked Jira issue.
 */
export class DatabasesSmartLinkClient extends CardClient {
  private readonly listeners = new Map<string, Set<UpdateListener>>();
  private readonly pendingRefreshes = new Map<string, Promise<JsonLd.Response>>();
  private readonly refreshResponsesBeingPublished = new Map<string, JsonLd.Response>();

  /**
   * Keeps speculative Smart Card prefetch disabled. Visible cards remain the
   * request signal so Atlaskit can batch their ordinary resolutions together.
   */
  public override prefetchData = (_url: string, _appearance?: CardAppearance) =>
    Promise.resolve(undefined);

  /**
   * Delegates ordinary resolution, caching, deduplication, and per-host
   * batching to Atlaskit. During an explicit refresh notification, a forced
   * synchronous reload consumes the already-fetched response.
   */
  public override async fetchData(
    url: string,
    force = false,
    appearance?: CardAppearance,
  ): Promise<JsonLd.Response> {
    const publishedRefresh = force
      ? this.refreshResponsesBeingPublished.get(url)
      : undefined;
    if (publishedRefresh) {
      return publishedRefresh;
    }

    reportResolverCheckpoint();

    try {
      return await super.fetchData(url, force, appearance);
    } catch (error) {
      reportResolverCheckpoint();
      throw normalizeResolverError(error);
    }
  }

  /**
   * Performs a cache-bypassing resolver request after a mutation. Concurrent
   * refreshes for the same URL share one request, and rejected requests remain
   * retryable after the pending entry is cleared.
   */
  public forceRefreshData(url: string): Promise<JsonLd.Response> {
    const pendingRefresh = this.pendingRefreshes.get(url);
    if (pendingRefresh) {
      return pendingRefresh;
    }

    const refresh = this.refreshAndNotify(url).finally(() => {
      this.pendingRefreshes.delete(url);
    });

    this.pendingRefreshes.set(url, refresh);
    return refresh;
  }

  /**
   * Registers a URL-scoped listener for successful explicit refreshes.
   * Unsubscribe during consumer cleanup to release the listener.
   */
  public subscribeToUpdates(
    url: string,
    callback: UpdateListener,
  ): UnsubscribeUpdateListener {
    const callbacks = this.listeners.get(url) ?? new Set<UpdateListener>();

    if (callbacks.has(callback)) {
      throw new Error(`The same callback was added to ${url} more than once`);
    }

    callbacks.add(callback);
    this.listeners.set(url, callbacks);

    return () => {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.listeners.delete(url);
      }
    };
  }

  private async refreshAndNotify(url: string): Promise<JsonLd.Response> {
    const response = await this.fetchFreshData(url);

    this.refreshResponsesBeingPublished.set(url, response);

    try {
      this.notifyListeners(url);
    } finally {
      this.refreshResponsesBeingPublished.delete(url);
    }

    return response;
  }

  private async fetchFreshData(url: string): Promise<JsonLd.Response> {
    reportResolverCheckpoint();

    try {
      return await this.fetchWithIgnoreCache(url);
    } catch (error) {
      reportResolverCheckpoint();
      throw normalizeResolverError(error);
    }
  }

  private notifyListeners(url: string): void {
    const listenerErrors: unknown[] = [];

    this.listeners.get(url)?.forEach((listener) => {
      const result = invokeListener(listener);

      if (!result.succeeded) {
        listenerErrors.push(result.error);
      }
    });

    if (listenerErrors.length > 0) {
      throw listenerErrors[0];
    }
  }

  private async fetchWithIgnoreCache(url: string): Promise<JsonLd.Response> {
    const response = await globalThis.fetch(RESOLVER_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([{ resourceUrl: url, ignoreCachedValue: true }]),
    });

    return readResolverResponse(response, url);
  }
}
